import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiCalendar,
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiEye,
  FiUser,
  FiDollarSign,
  FiMapPin,
  FiRefreshCw,
  FiCheck,
  FiPlay,
  FiCheckCircle,
  FiCreditCard,
  FiDownload,
  FiX,
  FiUsers
} from 'react-icons/fi';

const Reservations = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [cars, setCars] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'Credit Card',
    notes: ''
  });
  const [formData, setFormData] = useState({
    car: '',
    client: '',
    startDate: '',
    endDate: '',
    dailyPrice: '',
    deposit: '',
    paymentMethod: 'Credit Card',
    pickupLocation: '',
    returnLocation: '',
    initialMileage: '',
    fuelLevel: 'Full',
    notes: '',
    // Second driver fields - simplified to just store client ID
    hasSecondDriver: false,
    secondDriver: null // Will store the selected client object
  });

  // Add state for searchable dropdowns
  const [carSearchTerm, setCarSearchTerm] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showCarDropdown, setShowCarDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Add state for second driver search
  const [secondDriverSearchTerm, setSecondDriverSearchTerm] = useState('');
  const [showSecondDriverDropdown, setShowSecondDriverDropdown] = useState(false);

  // Add state for calculated values
  const [calculatedValues, setCalculatedValues] = useState({
    days: 0,
    totalPrice: 0
  });

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReservations, setTotalReservations] = useState(0);
  const [reservationsPerPage] = useState(9); // Show 9 cards per page

  // Filter cars and clients based on search terms
  const filteredCars = cars.filter(car =>
    car.brand.toLowerCase().includes(carSearchTerm.toLowerCase()) ||
    car.model.toLowerCase().includes(carSearchTerm.toLowerCase()) ||
    car.licensePlate.toLowerCase().includes(carSearchTerm.toLowerCase()) ||
    car.year.toString().includes(carSearchTerm) ||
    car.dailyPrice.toString().includes(carSearchTerm)
  ).slice(0, 10); // Limit to 10 results for better performance

  // Separate available and rented cars for better UX
  const availableCars = filteredCars.filter(car => car.status === 'Available');
  const rentedCars = filteredCars.filter(car => car.status === 'Rented');
  const maintenanceCars = filteredCars.filter(car => car.status === 'Maintenance');

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
    client.phone?.toLowerCase().includes(clientSearchTerm.toLowerCase())
  ).slice(0, 10); // Limit to 10 results for better performance

  // Separate filtered clients for second driver search
  const filteredSecondDriverClients = clients.filter(client => {
    // Exclude the main client from second driver selection
    if (formData.client && client._id === formData.client) {
      return false;
    }

    if (!secondDriverSearchTerm.trim()) return true; // Show all available clients when search is empty

    const searchTerm = secondDriverSearchTerm.toLowerCase().trim();
    const clientName = (client.name || '').toLowerCase();
    const clientEmail = (client.email || '').toLowerCase();
    const clientPhone = (client.phone || '').toLowerCase();

    return clientName.includes(searchTerm) ||
      clientEmail.includes(searchTerm) ||
      clientPhone.includes(searchTerm);
  }).slice(0, 10); // Limit to 10 results for better performance

  // Debug logging
  console.log('Total clients loaded:', clients.length);
  console.log('Second driver search term:', secondDriverSearchTerm);
  console.log('Filtered second driver clients:', filteredSecondDriverClients.length);

  // Handle car selection
  const handleCarSelect = (car) => {
    setFormData({
      ...formData,
      car: car._id,
      dailyPrice: car.dailyPrice.toString()
    });
    setCarSearchTerm(`${car.year} ${car.brand} ${car.model} - ${car.licensePlate}`);
    setShowCarDropdown(false);
  };

  // Handle client selection
  const handleClientSelect = async (client) => {
    // Check if client is blacklisted
    try {
      const blacklistResponse = await api.get(`/api/blacklist/check/${client._id}`);

      if (blacklistResponse.data.isBlacklisted) {
        if (blacklistResponse.data.blacklistedBy === 'other') {
          const warningMessage = `⚠️ ATTENTION: Ce client est blacklisté par d'autres agences!\n\n`;
          const agenciesInfo = blacklistResponse.data.blacklistedByAgencies.map(agency =>
            `• ${agency.agencyName}: ${agency.reason}`
          ).join('\n');
          const fullMessage = warningMessage + agenciesInfo + '\n\nVoulez-vous quand même sélectionner ce client comme conducteur principal ?';

          if (!window.confirm(fullMessage)) {
            return; // User cancelled
          }
        } else if (blacklistResponse.data.blacklistedBy === 'current') {
          const warningMessage = `⚠️ ATTENTION: Ce client est blacklisté par votre agence pour la raison: "${blacklistResponse.data.reason}".\n\nVoulez-vous quand même sélectionner ce client comme conducteur principal ?`;

          if (!window.confirm(warningMessage)) {
            return; // User cancelled
          }
        }
      }

      setFormData({
        ...formData,
        client: client._id,
        // Clear second driver if it's the same as the new main client
        secondDriver: formData.secondDriver && formData.secondDriver._id === client._id ? null : formData.secondDriver
      });
      setClientSearchTerm(`${client.name} - ${client.email}`);
      setShowClientDropdown(false);

      // Clear second driver search term if it was the same client
      if (formData.secondDriver && formData.secondDriver._id === client._id) {
        setSecondDriverSearchTerm('');
      }
    } catch (error) {
      console.error('Error checking blacklist:', error);
      // If blacklist check fails, still allow selection but show warning
      toast.error('Impossible de vérifier le statut de blacklist. Sélection du client autorisée.');

      setFormData({
        ...formData,
        client: client._id,
        // Clear second driver if it's the same as the new main client
        secondDriver: formData.secondDriver && formData.secondDriver._id === client._id ? null : formData.secondDriver
      });
      setClientSearchTerm(`${client.name} - ${client.email}`);
      setShowClientDropdown(false);

      // Clear second driver search term if it was the same client
      if (formData.secondDriver && formData.secondDriver._id === client._id) {
        setSecondDriverSearchTerm('');
      }
    }
  };

  // Handle second driver selection
  const handleSecondDriverSelect = async (client) => {
    // Check if client is blacklisted
    try {
      const blacklistResponse = await api.get(`/api/blacklist/check/${client._id}`);

      if (blacklistResponse.data.isBlacklisted) {
        if (blacklistResponse.data.blacklistedBy === 'other') {
          const warningMessage = `⚠️ ATTENTION: Ce client est blacklisté par d'autres agences!\n\n`;
          const agenciesInfo = blacklistResponse.data.blacklistedByAgencies.map(agency =>
            `• ${agency.agencyName}: ${agency.reason}`
          ).join('\n');
          const fullMessage = warningMessage + agenciesInfo + '\n\nVoulez-vous quand même sélectionner ce client comme deuxième conducteur ?';

          if (!window.confirm(fullMessage)) {
            return; // User cancelled
          }
        } else if (blacklistResponse.data.blacklistedBy === 'current') {
          const warningMessage = `⚠️ ATTENTION: Ce client est blacklisté par votre agence pour la raison: "${blacklistResponse.data.reason}".\n\nVoulez-vous quand même sélectionner ce client comme deuxième conducteur ?`;

          if (!window.confirm(warningMessage)) {
            return; // User cancelled
          }
        }
      }

      setFormData({
        ...formData,
        secondDriver: client
      });
      setSecondDriverSearchTerm(`${client.name} - ${client.email}`);
      setShowSecondDriverDropdown(false);
    } catch (error) {
      console.error('Error checking blacklist:', error);
      // If blacklist check fails, still allow selection but show warning
      toast.error('Impossible de vérifier le statut de blacklist. Sélection du client autorisée.');

      setFormData({
        ...formData,
        secondDriver: client
      });
      setSecondDriverSearchTerm(`${client.name} - ${client.email}`);
      setShowSecondDriverDropdown(false);
    }
  };

  // Clear car search
  const clearCarSearch = () => {
    setCarSearchTerm('');
    setFormData({ ...formData, car: '', dailyPrice: '' });
    setShowCarDropdown(false);
  };

  // Clear client search
  const clearClientSearch = () => {
    setClientSearchTerm('');
    setFormData({ ...formData, client: '' });
    setShowClientDropdown(false);
  };

  // Clear second driver search
  const clearSecondDriverSearch = () => {
    setSecondDriverSearchTerm('');
    setFormData({
      ...formData,
      secondDriver: null
    });
    setShowSecondDriverDropdown(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowCarDropdown(false);
        setShowClientDropdown(false);
        setShowSecondDriverDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate days and total price when dates or daily price change
  useEffect(() => {
    if (formData.startDate && formData.endDate && formData.dailyPrice) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const dailyPrice = parseFloat(formData.dailyPrice);

      if (!isNaN(dailyPrice) && startDate < endDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const days = Math.ceil(timeDiff / (1000 * 3600 * 24));
        const totalPrice = days * dailyPrice;

        setCalculatedValues({
          days: days,
          totalPrice: totalPrice
        });
      } else {
        setCalculatedValues({
          days: 0,
          totalPrice: 0
        });
      }
    } else {
      setCalculatedValues({
        days: 0,
        totalPrice: 0
      });
    }
  }, [formData.startDate, formData.endDate, formData.dailyPrice]);

  const fetchData = async (page = 1) => {
    try {
      const [reservationsRes, carsRes, clientsRes] = await Promise.all([
        api.get(`/api/reservations?page=${page}&limit=${reservationsPerPage}`),
        api.get('/api/cars'),
        api.get('/api/clients')
      ]);

      setReservations(reservationsRes.data.data);
      setCars(carsRes.data.data);
      setClients(clientsRes.data.data);

      // Update pagination state
      setTotalReservations(reservationsRes.data.count || 0);
      setTotalPages(Math.ceil((reservationsRes.data.count || 0) / reservationsPerPage));
      setCurrentPage(page);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Échec de la récupération des données');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that selected car is available
    const selectedCar = cars.find(car => car._id === formData.car);
    if (selectedCar && selectedCar.status !== 'Available') {
      toast.error(`La voiture sélectionnée (${selectedCar.brand} ${selectedCar.model}) n'est pas disponible pour la location. Statut: ${selectedCar.status === 'Rented' ? 'Déjà louée' : 'En maintenance'}`);
      return;
    }

    // Validate that second driver is different from main client
    if (formData.hasSecondDriver && formData.secondDriver && formData.client === formData.secondDriver._id) {
      toast.error('Le deuxième conducteur doit être différent du conducteur principal');
      return;
    }

    // Validate dates
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }

    // Validate calculated values
    if (calculatedValues.days <= 0 || calculatedValues.totalPrice <= 0) {
      toast.error('Veuillez vérifier vos dates et prix journalier');
      return;
    }

    const reservationData = {
      ...formData,
      dailyPrice: parseFloat(formData.dailyPrice),
      deposit: parseFloat(formData.deposit) || 0,
      initialMileage: formData.initialMileage ? parseInt(formData.initialMileage) : undefined,
      totalPrice: calculatedValues.totalPrice // Use calculated total price
    };

    // Debug logging for form submission
    console.log('Form Data:', formData);
    console.log('Reservation Data being sent:', reservationData);
    console.log('Second Driver in form data:', formData.secondDriver);

    try {
      const response = await api.post('/api/reservations', reservationData);

      toast.success('Réservation créée avec succès');
      setShowAddModal(false);
      resetForm();
      setCalculatedValues({ days: 0, totalPrice: 0 });
      fetchData(1); // Retour à la première page après création
    } catch (error) {
      console.error('Error creating reservation:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de créer des réservations.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Échec de la création de la réservation. Veuillez vérifier vos données.');
      }
    }
  };

  const handleDelete = async (id) => {
    // Find the reservation to check its status
    const reservation = reservations.find(r => r._id === id);
    if (!reservation) {
      toast.error('Réservation non trouvée');
      return;
    }

    // Security check: Only allow deletion of pending and completed reservations
    if (reservation.status === 'Active') {
      toast.error('Impossible de supprimer une réservation en cours. Terminez-la d\'abord.');
      return;
    }

    const confirmationMessage =
      'Êtes-vous sûr de vouloir supprimer cette réservation ?\n\n' +
      '⚠️  ATTENTION : Toutes les photos "Avant" associées à cette réservation seront supprimées immédiatement.\n\n' +
      'Note : Si la réservation était terminée, les photos auraient été supprimées automatiquement après +1 jour.\n\n' +
      'Cette action est irréversible.';

    if (window.confirm(confirmationMessage)) {
      try {
        await api.delete(`/api/reservations/${id}`);
        toast.success('Réservation et photos supprimées avec succès');
        fetchData(currentPage); // Maintenir la page actuelle après suppression
      } catch (error) {
        console.error('Error deleting reservation:', error);
        toast.error('Échec de la suppression de la réservation');
      }
    }
  };


  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchData(newPage);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await api.put(`/api/reservations/${id}/status`, { status: newStatus });
      toast.success(`Statut de la réservation mis à jour vers ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Error updating reservation status:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de mettre à jour le statut de la réservation.');
      } else {
        toast.error('Échec de la mise à jour du statut de la réservation.');
      }
    }
  };

  const handlePaymentUpdate = async (id, newPaymentStatus) => {
    try {
      const response = await api.put(`/api/reservations/${id}/payment-status`, { paymentStatus: newPaymentStatus });
      toast.success(`Statut de paiement de la réservation mis à jour vers ${newPaymentStatus}`);
      fetchData();
    } catch (error) {
      console.error('Error updating reservation payment status:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de mettre à jour le statut de paiement de la réservation.');
      } else {
        toast.error('Échec de la mise à jour du statut de paiement de la réservation.');
      }
    }
  };

  const openPaymentModal = (reservation) => {
    setSelectedReservation(reservation);
    setPaymentData({
      amount: reservation.totalPrice || 0,
      paymentMethod: 'Credit Card',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const openContractModal = (reservation) => {
    setSelectedReservation(reservation);
    setShowContractModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!selectedReservation) return;

    try {
      // Create payment record
      const paymentResponse = await api.post('/api/payments', {
        reservation: selectedReservation._id,
        client: selectedReservation.client._id,
        amount: parseFloat(paymentData.amount),
        paymentMethod: paymentData.paymentMethod,
        paymentType: 'Rental Fee',
        notes: paymentData.notes
      });

      // Update reservation payment status to Paid
      await api.put(`/api/reservations/${selectedReservation._id}/payment-status`, {
        paymentStatus: 'Paid'
      });

      toast.success('Paiement traité avec succès !');
      setShowPaymentModal(false);
      setSelectedReservation(null);
      setPaymentData({ amount: '', paymentMethod: 'Credit Card', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Échec du traitement du paiement');
    }
  };

  const resetForm = () => {
    setFormData({
      car: '',
      client: '',
      startDate: '',
      endDate: '',
      dailyPrice: '',
      deposit: '',
      paymentMethod: 'Credit Card',
      pickupLocation: '',
      returnLocation: '',
      initialMileage: '',
      fuelLevel: 'Full',
      notes: '',
      // Second driver fields - simplified to just store client ID
      hasSecondDriver: false,
      secondDriver: null // Will store the selected client object
    });
    setCalculatedValues({ days: 0, totalPrice: 0 });
    setCarSearchTerm('');
    setClientSearchTerm('');
    setSecondDriverSearchTerm('');
    setShowCarDropdown(false);
    setShowClientDropdown(false);
    setShowSecondDriverDropdown(false);
  };

  const handleEdit = (reservation) => {
    // Security check: Only allow editing pending reservations
    if (reservation.status !== 'Pending') {
      toast.error(`Impossible de modifier une réservation ${reservation.status === 'Active' ? 'en cours' : 'terminée'}. Seules les réservations en attente peuvent être modifiées.`);
      return;
    }

    setEditingReservation(reservation);
    setFormData({
      car: reservation.car?._id || '',
      client: reservation.client?._id || '',
      startDate: reservation.startDate ? new Date(reservation.startDate).toISOString().split('T')[0] : '',
      endDate: reservation.endDate ? new Date(reservation.endDate).toISOString().split('T')[0] : '',
      dailyPrice: reservation.dailyPrice?.toString() || '',
      deposit: reservation.deposit?.toString() || '',
      paymentMethod: reservation.paymentMethod || 'Credit Card',
      pickupLocation: reservation.pickupLocation || '',
      returnLocation: reservation.returnLocation || '',
      initialMileage: reservation.initialMileage?.toString() || '',
      fuelLevel: reservation.fuelLevel || 'Full',
      notes: reservation.notes || '',
      // Second driver fields - simplified to just store client ID
      hasSecondDriver: reservation.secondDriver ? true : false,
      secondDriver: reservation.secondDriver || null // Will store the selected client object
    });

    // Set search terms for display
    if (reservation.car) {
      setCarSearchTerm(`${reservation.car.year} ${reservation.car.brand} ${reservation.car.model} - ${reservation.car.licensePlate}`);
    }
    if (reservation.client) {
      setClientSearchTerm(`${reservation.client.name} - ${reservation.client.email}`);
    }
    if (reservation.secondDriver) {
      setSecondDriverSearchTerm(`${reservation.secondDriver.name} - ${reservation.secondDriver.email}`);
    }

    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    // Validate that selected car is available
    const selectedCar = cars.find(car => car._id === formData.car);
    if (selectedCar && selectedCar.status !== 'Available') {
      toast.error(`La voiture sélectionnée (${selectedCar.brand} ${selectedCar.model}) n'est pas disponible pour la location. Statut: ${selectedCar.status === 'Rented' ? 'Déjà louée' : 'En maintenance'}`);
      return;
    }

    // Validate that second driver is different from main client
    if (formData.hasSecondDriver && formData.secondDriver && formData.client === formData.secondDriver._id) {
      toast.error('Le deuxième conducteur doit être différent du conducteur principal');
      return;
    }

    // Validate dates
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }

    // Validate calculated values
    if (calculatedValues.days <= 0 || calculatedValues.totalPrice <= 0) {
      toast.error('Veuillez vérifier vos dates et prix journalier');
      return;
    }

    const reservationData = {
      ...formData,
      dailyPrice: parseFloat(formData.dailyPrice),
      deposit: parseFloat(formData.deposit) || 0,
      initialMileage: formData.initialMileage ? parseInt(formData.initialMileage) : undefined,
      totalPrice: calculatedValues.totalPrice
    };

    try {
      const response = await api.put(`/api/reservations/${editingReservation._id}`, reservationData);

      toast.success('Réservation mise à jour avec succès');
      setShowEditModal(false);
      setEditingReservation(null);
      resetForm();
      setCalculatedValues({ days: 0, totalPrice: 0 });
      fetchData();
    } catch (error) {
      console.error('Error updating reservation:', error);
      if (error.response?.status === 401) {
        toast.error('Authentification requise. Veuillez vous reconnecter.');
      } else if (error.response?.status === 403) {
        toast.error('Vous n\'avez pas la permission de mettre à jour des réservations.');
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Échec de la mise à jour de la réservation. Veuillez vérifier vos données.');
      }
    }
  };

  const filteredReservations = reservations.filter((reservation) => {
    const matchesSearch =
      reservation.car?.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reservation.car?.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reservation.client?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reservation.status === statusFilter;
    const matchesPaymentStatus = paymentStatusFilter === 'all' || reservation.paymentStatus === paymentStatusFilter;
    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800';
      case 'Confirmed': return 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 border border-primary-100 dark:border-primary-800';
      case 'Active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border border-green-200 dark:border-green-800';
      case 'Completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Overdue': return 'bg-orange-100 text-orange-800 dark:bg-red-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Partial': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Unpaid': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Refunded': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Pending': return 'En attente';
      case 'Confirmed': return 'Confirmé';
      case 'Active': return 'En cours';
      case 'Completed': return 'Terminé';
      case 'Cancelled': return 'Annulé';
      case 'Overdue': return 'En retard';
      default: return status;
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'Paid': return 'Payé';
      case 'Partial': return 'Partiel';
      case 'Unpaid': return 'Non payé';
      case 'Refunded': return 'Remboursé';
      default: return status;
    }
  };

  // Get status-specific information and actions
  const getStatusInfo = (status) => {
    switch (status) {
      case 'Pending':
        return {
          color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20',
          text: 'En attente',
          actions: 'Modifiable et supprimable',
          icon: '⏳'
        };
      case 'Active':
        return {
          color: 'text-green-600 bg-green-100 dark:bg-green-900/20',
          text: 'En cours',
          actions: 'Non modifiable - Terminez d\'abord',
          icon: '🚗'
        };
      case 'Completed':
        return {
          color: 'text-primary-600 bg-primary-100 dark:bg-primary-900/20',
          text: 'Terminée',
          actions: 'Non modifiable - Supprimable',
          icon: '✅'
        };
      case 'Cancelled':
        return {
          color: 'text-red-600 bg-red-100 dark:bg-red-900/20',
          text: 'Annulée',
          actions: 'Non modifiable - Supprimable',
          icon: '❌'
        };
      default:
        return {
          color: 'text-gray-600 bg-gray-100 dark:bg-gray-900/20',
          text: status,
          actions: 'Actions limitées',
          icon: '❓'
        };
    }
  };

  const getPaymentMethodText = (method) => {
    switch (method) {
      case 'Credit Card': return 'Carte de crédit';
      case 'Debit Card': return 'Carte de débit';
      case 'Cash': return 'Espèces';
      case 'Online Payment': return 'Paiement en ligne';
      case 'Bank Transfer': return 'Virement bancaire';
      default: return method;
    }
  };

  const getFuelLevelText = (level) => {
    switch (level) {
      case 'Full': return 'Plein';
      case 'Three Quarters': return 'Trois quarts';
      case 'Half': return 'Moitié';
      case 'Quarter': return 'Quart';
      case 'Empty': return 'Vide';
      default: return level;
    }
  };





  const downloadContract = async (reservation) => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Set very small font sizes for compact fit
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);

      // Logo section at the top
      doc.setFillColor(128, 128, 128);
      doc.rect(10, 5, 190, 20, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.text('CONTRAT', 105, 18, { align: 'center' }, { font: 'bold' });

      // Main title with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 28, 190, 10, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.text('LOCATION DE VOITURE', 105, 35, { align: 'center' });

      // Contract number and date with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 42, 90, 8, 'F');
      doc.rect(110, 42, 90, 8, 'F');
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text(`CONTRAT N°: ${reservation._id.slice(-8).toUpperCase()}`, 15, 47);
      doc.text(`Date: ${new Date().toLocaleDateString('en-GB')}`, 115, 47);

      // Renter Information with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 54, 90, 6, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.setFontSize(10);
      doc.text('INFORMATIONS LOCATAIRE', 55, 58, { align: 'center' }, { font: 'bold' });

      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('Nom:', 12, 66);
      doc.text(reservation.client?.name || 'N/A', 95, 66, { align: 'right' }, { font: 'bold' });
      doc.text('Date de naissance:', 12, 72);
      doc.text(reservation.client?.dateOfBirth ? new Date(reservation.client.dateOfBirth).toLocaleDateString() : 'N/A', 95, 72, { align: 'right' });
      doc.text('C.I.N', 12, 78);
      doc.text(reservation.client?.cin || 'N/A', 95, 78, { align: 'right' }, { font: 'bold' });
      doc.text('Pays:', 12, 84);
      doc.text(reservation.client?.address?.country || 'N/A', 95, 84, { align: 'right' }, { font: 'bold' });
      doc.text('Permis de conduire N°:', 12, 90);
      doc.text(reservation.client?.driverLicenseNumber || 'N/A', 95, 90, { align: 'right' }, { font: 'bold' });
      doc.text('Adresse:', 12, 96);
      const address = reservation.client?.address?.street ?
        `${reservation.client.address.street}, ${reservation.client.address.city}, ${reservation.client.address.state} ${reservation.client.address.zipCode}` : 'N/A';
      doc.text(address, 95, 96, { align: 'right' }, { font: 'bold' });
      doc.text('Téléphone:', 12, 102);
      doc.text(reservation.client?.phone || 'N/A', 95, 102, { align: 'right' }, { font: 'bold' });
      doc.text('Email:', 12, 108);
      doc.text(reservation.client?.email || 'N/A', 95, 108, { align: 'right' }, { font: 'bold' });

      // Company Information with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(110, 54, 90, 6, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.setFontSize(10);
      doc.text('INFORMATIONS ENTREPRISE', 155, 58, { align: 'center' });

      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('Agence:', 112, 66);
      doc.text(user?.companyInfo?.agence || 'Agence', 195, 66, { align: 'right' });
      doc.text('Bureau:', 112, 72);
      doc.text(user?.companyInfo?.bureau || 'Bureau Principal', 195, 72, { align: 'right' });
      doc.text('Entreprise:', 112, 78);
      doc.text(user?.companyInfo?.entreprise || 'Gestionnaire Location', 195, 78, { align: 'right' });
      doc.text('Adresse:', 112, 84);
      doc.text(user?.companyInfo?.adresse || '123 Rue Principale', 195, 84, { align: 'right' });
      doc.text('Ville:', 112, 90);
      doc.text(user?.companyInfo?.ville || 'Ville - 12345', 195, 90, { align: 'right' });
      doc.text('Agent:', 112, 96);
      doc.text(user?.companyInfo?.agent || 'Gestionnaire Location', 195, 96, { align: 'right' });
      doc.text('Téléphone:', 112, 102);
      doc.text(user?.companyInfo?.telephone || '+1 (555) 123-4567', 195, 102, { align: 'right' });
      doc.text('Email:', 112, 108);
      doc.text(user?.companyInfo?.email || 'info@carrental.com', 195, 108, { align: 'right' });

      // Second Driver with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 118, 90, 6, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.setFontSize(10);
      doc.text('DEUXIÈME CONDUCTEUR', 55, 122, { align: 'center' });

      // Debug logging for second driver
      console.log('Second Driver Data:', reservation.secondDriver);
      console.log('Reservation Object:', reservation);

      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      if (reservation.secondDriver) {
        doc.text('Nom:', 12, 130);
        doc.text(reservation.secondDriver.name || 'N/A', 95, 130, { align: 'right' });
        doc.text('Date de naissance:', 12, 136);
        doc.text(reservation.secondDriver.dateOfBirth ? new Date(reservation.secondDriver.dateOfBirth).toLocaleDateString() : 'N/A', 95, 136, { align: 'right' });
        doc.text('ID/Passeport:', 12, 142);
        doc.text(reservation.secondDriver.driverLicenseNumber || 'N/A', 95, 142, { align: 'right' });
        doc.text('Pays:', 12, 148);
        doc.text('Maroc', 95, 148, { align: 'right' });
        doc.text('Permis de conduire N°:', 12, 154);
        doc.text(reservation.secondDriver.driverLicenseNumber || 'N/A', 85, 154, { align: 'right' });
        doc.text('Adresse:', 12, 160);
        const address = reservation.secondDriver.address?.street ?
          `${reservation.secondDriver.address.street}, ${reservation.secondDriver.address.city}, ${reservation.secondDriver.address.state} ${reservation.secondDriver.address.zipCode}` : 'N/A';
        doc.text(address, 95, 160, { align: 'right' });
        doc.text('Téléphone:', 12, 166);
        doc.text(reservation.secondDriver.phone || 'N/A', 95, 166, { align: 'right' });
        doc.text('Email:', 12, 172);
        doc.text(reservation.secondDriver.email || 'N/A', 95, 172, { align: 'right' });
      } else {
        doc.text('Nom:', 12, 130);
        doc.text('AUCUN', 95, 130, { align: 'right' });
        doc.text('Date de naissance:', 12, 136);
        doc.text('N/A', 95, 136, { align: 'right' });
        doc.text('ID/Passeport:', 12, 142);
        doc.text('N/A', 95, 142, { align: 'right' });
        doc.text('Pays:', 12, 148);
        doc.text('N/A', 95, 148, { align: 'right' });
        doc.text('Permis de conduire N°:', 12, 154);
        doc.text('N/A', 95, 154, { align: 'right' });
        doc.text('Adresse:', 12, 160);
        doc.text('N/A', 95, 160, { align: 'right' });
        doc.text('Téléphone:', 12, 166);
        doc.text('N/A', 95, 166, { align: 'right' });
        doc.text('Email:', 12, 172);
        doc.text('N/A', 95, 172, { align: 'right' });
      }

      // Vehicle Information with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(110, 118, 90, 6, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.setFontSize(10);
      doc.text('INFORMATIONS VÉHICULE', 155, 122, { align: 'center' });

      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('Marque/Modèle:', 112, 130);
      const vehicleBrandModel = (reservation.car?.brand && reservation.car?.model)
        ? `${reservation.car.brand}/${reservation.car.model}`
        : 'N/A';
      doc.text(vehicleBrandModel, 195, 130, { align: 'right' });
      doc.text('Code/Immatriculation:', 112, 136);
      doc.text(reservation.car?.licensePlate || 'N/A', 195, 136, { align: 'right' });
      doc.text('Type de carburant:', 112, 142);
      doc.text(reservation.car?.fuelType || 'ESSENCE', 195, 142, { align: 'right' });
      doc.text('Kilométrage départ:', 112, 148);
      doc.text(`${reservation.initialMileage || '0'} Km`, 195, 148, { align: 'right' });
      doc.text('Kilométrage retour:', 112, 154);
      doc.text('0 Km', 195, 154, { align: 'right' });
      doc.text('Franchise véhicule:', 112, 160);
      doc.text('500 MAD', 195, 160, { align: 'right' });
      doc.text('Mode de paiement:', 112, 166);
      doc.text('Payé en cas de dommage', 195, 166, { align: 'right' });

      // Rental Information with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 182, 90, 6, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.setFontSize(10);
      doc.text('INFORMATIONS LOCATION', 55, 186, { align: 'center' });

      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('Prix réservation:', 12, 194);
      doc.text(`${reservation.totalPrice?.toFixed(2) || '0.00'} MAD`, 95, 194, { align: 'right' });
      doc.text('Reste à payer:', 12, 200);
      doc.text(`${reservation.totalPrice?.toFixed(2) || '0.00'} MAD`, 95, 200, { align: 'right' });
      doc.text('Date & heure départ:', 12, 206);
      doc.text(`${new Date(reservation.startDate).toLocaleDateString('fr-FR')} à 14:30`, 95, 206, { align: 'right' });
      doc.text('Date & heure retour:', 12, 212);
      doc.text(`${new Date(reservation.endDate).toLocaleDateString('fr-FR')} à 14:30`, 95, 212, { align: 'right' });

      // Rental Details with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(110, 182, 90, 6, 'F');
      doc.setTextColor(44, 62, 80); // Dark text
      doc.setFontSize(10);
      doc.text('DÉTAILS LOCATION', 155, 186, { align: 'center' });

      const duration = Math.ceil((new Date(reservation.endDate) - new Date(reservation.startDate)) / (1000 * 60 * 60 * 24));
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(10);
      doc.text(`(${duration} JOURS)`, 155, 194, { align: 'center' });
      doc.setFontSize(8);
      doc.text('Prix supplémentaire:', 112, 200);
      doc.text('AUCUN', 195, 200, { align: 'right' });
      doc.text('Lieu de départ:', 112, 206);
      doc.text(reservation.pickupLocation || user?.companyInfo?.bureau || 'Bureau Principal', 195, 206, { align: 'right' });
      doc.text('Lieu de retour:', 112, 212);
      doc.text(reservation.returnLocation || user?.companyInfo?.bureau || 'Bureau Principal', 195, 212, { align: 'right' });

      // Disclaimer with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 220, 190, 8, 'F');
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('IMPORTANT: Le locataire est seul responsable des infractions de circulation et de toute poursuite légale ou douanière à son encontre.', 105, 225, { align: 'center' });

      // Declaration with clean styling
      doc.setFillColor(248, 250, 252); // Light gray background
      doc.rect(10, 232, 190, 6, 'F');
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('Je déclare avoir lu les conditions générales mentionnées dans le contrat de location de voiture et les accepter sans réserve.', 105, 235, { align: 'center' });

      // Date and Location - smaller
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text(`À Ville le ${new Date().toLocaleDateString('fr-FR')}`, 105, 245, { align: 'center' });

      // Signatures with clean styling
      doc.setDrawColor(156, 163, 175); // Gray border color
      doc.setLineWidth(0.3);
      doc.rect(10, 248, 90, 20);
      doc.rect(110, 248, 90, 20);

      doc.setTextColor(44, 62, 80);
      doc.setFontSize(8);
      doc.text('Signature Client', 55, 260, { align: 'center' });
      doc.text('SIGNATURE ENTREPRISE', 155, 260, { align: 'center' });

      // Save the PDF
      doc.save(`contract-${reservation._id.slice(-8)}.pdf`);
      toast.success('Contract downloaded successfully!');
    } catch (error) {
      console.error('Error generating contract:', error);
      toast.error('Error generating contract');
    }
  };

  const downloadAllContracts = async () => {
    try {
      const { jsPDF } = await import('jspdf');

      for (let i = 0; i < filteredReservations.length; i++) {
        const reservation = filteredReservations[i];
        const doc = new jsPDF();

        // Set very small font sizes for compact fit
        doc.setFontSize(12);
        doc.setTextColor(44, 62, 80);

        // Logo section at the top
        doc.setFillColor(128, 128, 128);
        doc.rect(10, 5, 190, 20, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.text('CONTRAT', 105, 18, { align: 'center' }, { font: 'bold' });

        // Main title with clean styling
        doc.setFillColor(248, 250, 252); // Light gray background
        doc.rect(10, 28, 190, 10, 'F');
        doc.setTextColor(44, 62, 80); // Dark text
        doc.text('LOCATION DE VOITURE sans chauffeur', 105, 35, { align: 'center' });

        // Contract number and date with light gray background - smaller
        doc.setFillColor(236, 240, 241);
        doc.rect(10, 42, 90, 8, 'F');
        doc.rect(110, 42, 90, 8, 'F');
        doc.setTextColor(44, 62, 80);
        doc.setFontSize(10);
        doc.text(`CONTRAT N°: ${reservation._id.slice(-8).toUpperCase()}`, 15, 47);
        doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 115, 47);



        // Renter Information (Blue header) - smaller
        doc.setFillColor(52, 152, 219);
        doc.rect(10, 54, 90, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text('INFORMATIONS LOCATAIRE', 55, 58, { align: 'center' }, { font: 'bold' });

        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text('Nom:', 12, 66);
        doc.text(reservation.client?.name || 'N/A', 85, 66, { align: 'right' });
        doc.text('Date de naissance:', 12, 72);
        doc.text(reservation.client?.dateOfBirth ? new Date(reservation.client.dateOfBirth).toLocaleDateString() : 'N/A', 85, 72, { align: 'right' });
        doc.text('C.I.N', 12, 78);
        doc.text(reservation.client?.cin || 'N/A', 195, 78, { align: 'right' });
        doc.text('Pays:', 12, 84);
        doc.text(reservation.client?.address?.country || 'N/A', 85, 84, { align: 'right' });
        doc.text('Permis de conduire N°:', 12, 90);
        doc.text(reservation.client?.driverLicenseNumber || 'N/A', 85, 90, { align: 'right' });
        doc.text('Adresse:', 12, 96);
        const address = reservation.client?.address?.street ?
          `${reservation.client.address.street}, ${reservation.client.address.city}, ${reservation.client.address.state} ${reservation.client.address.zipCode}` : 'N/A';
        doc.text(address, 85, 96, { align: 'right' });
        doc.text('Téléphone:', 12, 102);
        doc.text(reservation.client?.phone || 'N/A', 85, 102, { align: 'right' });
        doc.text('Email:', 12, 108);
        doc.text(reservation.client?.email || 'N/A', 85, 108, { align: 'right' });

        // Company Information with clean styling
        doc.setFillColor(248, 250, 252); // Light gray background
        doc.rect(110, 54, 90, 6, 'F');
        doc.setTextColor(44, 62, 80); // Dark text
        doc.setFontSize(10);
        doc.text('INFORMATIONS ENTREPRISE', 155, 58, { align: 'center' }, { font: 'bold' });

        doc.setTextColor(44, 62, 80);
        doc.setFontSize(6);
        doc.text('Agence:', 115, 66);
        doc.text(user?.companyInfo?.agence || 'Agence', 192, 66, { align: 'right' });
        doc.text('Bureau:', 115, 72);
        doc.text(user?.companyInfo?.bureau || 'Bureau Principal', 192, 72, { align: 'right' });
        doc.text('Entreprise:', 115, 78);
        doc.text(user?.companyInfo?.entreprise || 'Gestionnaire Location', 192, 78, { align: 'right' });
        doc.text('Adresse:', 115, 84);
        doc.text(user?.companyInfo?.adresse || '123 Rue Principale', 192, 84, { align: 'right' });
        doc.text('Ville:', 115, 90);
        doc.text(user?.companyInfo?.ville || 'Ville - 12345', 192, 90, { align: 'right' });
        doc.text('Agent:', 115, 96);
        doc.text(user?.companyInfo?.agent || 'Gestionnaire Location', 192, 96, { align: 'right' });
        doc.text('Téléphone:', 115, 102);
        doc.text(user?.companyInfo?.telephone || '+1 (555) 123-4567', 192, 102, { align: 'right' });
        doc.text('Email:', 115, 108);
        doc.text(user?.companyInfo?.email || 'info@carrental.com', 192, 108, { align: 'right' });

        // Second Driver (Orange header) - smaller
        doc.setFillColor(230, 126, 34);
        doc.rect(10, 118, 90, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text('DEUXIÈME CONDUCTEUR', 55, 122, { align: 'center' }, { font: 'bold' });

        // Debug logging for second driver
        console.log('Second Driver Data:', reservation.secondDriver);
        console.log('Reservation Object:', reservation);

        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        if (reservation.secondDriver) {
          doc.text('Nom:', 12, 130);
          doc.text(reservation.secondDriver.name || 'N/A', 85, 130, { align: 'right' });
          doc.text('Date de naissance:', 12, 136);
          doc.text(reservation.secondDriver.dateOfBirth ? new Date(reservation.secondDriver.dateOfBirth).toLocaleDateString() : 'N/A', 85, 136, { align: 'right' });
          doc.text('ID/Passeport:', 12, 142);
          doc.text(reservation.secondDriver.driverLicenseNumber || 'N/A', 85, 142, { align: 'right' });
          doc.text('Pays:', 12, 148);
          doc.text('Maroc', 85, 148, { align: 'right' });
          doc.text('Permis de conduire N°:', 12, 154);
          doc.text(reservation.secondDriver.driverLicenseNumber || 'N/A', 85, 154, { align: 'right' });
          doc.text('Adresse:', 12, 160);
          const address = reservation.secondDriver.address?.street ?
            `${reservation.secondDriver.address.street}, ${reservation.secondDriver.address.city}, ${reservation.secondDriver.address.state} ${reservation.secondDriver.address.zipCode}` : 'N/A';
          doc.text(address, 85, 160, { align: 'right' });
          doc.text('Téléphone:', 12, 166);
          doc.text(reservation.secondDriver.phone || 'N/A', 85, 166, { align: 'right' });
          doc.text('Email:', 12, 172);
          doc.text(reservation.secondDriver.email || 'N/A', 85, 172, { align: 'right' });
        } else {
          doc.text('Nom:', 12, 130);
          doc.text('AUCUN', 85, 130, { align: 'right' });
          doc.text('Date de naissance:', 12, 136);
          doc.text('N/A', 85, 136, { align: 'right' });
          doc.text('ID/Passeport:', 12, 142);
          doc.text('N/A', 85, 142, { align: 'right' });
          doc.text('Pays:', 12, 148);
          doc.text('N/A', 85, 148, { align: 'right' });
          doc.text('Permis de conduire N°:', 12, 154);
          doc.text('N/A', 85, 154, { align: 'right' });
          doc.text('Adresse:', 12, 160);
          doc.text('N/A', 85, 160, { align: 'right' });
          doc.text('Téléphone:', 12, 166);
          doc.text('N/A', 85, 166, { align: 'right' });
          doc.text('Email:', 12, 172);
          doc.text('N/A', 85, 172, { align: 'right' });
        }

        // Vehicle Information (Purple header) - smaller
        doc.setFillColor(155, 89, 182);
        doc.rect(110, 118, 90, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text('INFORMATIONS VÉHICULE', 155, 122, { align: 'center' }, { font: 'bold' });

        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text('Marque/Modèle:', 112, 130);
        const vehicleBrandModel = (reservation.car?.brand && reservation.car?.model)
          ? `${reservation.car.brand}/${reservation.car.model}`
          : 'N/A';
        doc.text(vehicleBrandModel, 195, 130, { align: 'right' });
        doc.text('Code/Immatriculation:', 112, 136);
        doc.text(reservation.car?.licensePlate || 'N/A', 195, 136, { align: 'right' });
        doc.text('Type de carburant:', 112, 142);
        doc.text(reservation.car?.fuelType || 'ESSENCE', 195, 142, { align: 'right' });
        doc.text('Kilométrage départ:', 112, 148);
        doc.text(`${reservation.initialMileage || '0'} Km`, 195, 148, { align: 'right' });
        doc.text('Kilométrage retour:', 112, 154);
        doc.text('0 Km', 195, 154, { align: 'right' });
        doc.text('Franchise véhicule:', 112, 160);
        doc.text('500 MAD', 195, 160, { align: 'right' });
        doc.text('Mode de paiement:', 112, 166);
        doc.text('Payé en cas de dommage', 195, 166, { align: 'right' });

        // Rental Information (Red header) - smaller
        doc.setFillColor(231, 76, 60);
        doc.rect(10, 182, 90, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text('INFORMATIONS LOCATION', 55, 186, { align: 'center' }, { font: 'bold' });

        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text('Prix réservation:', 12, 194);
        doc.text(`${reservation.totalPrice?.toFixed(2) || '0.00'} MAD`, 85, 194, { align: 'right' });
        doc.text('Reste à payer:', 12, 200);
        doc.text(`${reservation.totalPrice?.toFixed(2) || '0.00'} MAD`, 85, 200, { align: 'right' });
        doc.text('Date & heure départ:', 12, 206);
        doc.text(`${new Date(reservation.startDate).toLocaleDateString('fr-FR')} à 14:30`, 85, 206, { align: 'right' });
        doc.text('Date & heure retour:', 12, 212);
        doc.text(`${new Date(reservation.endDate).toLocaleDateString('fr-FR')} à 14:30`, 85, 212, { align: 'right' });

        // Rental Details (Teal header) - smaller
        doc.setFillColor(26, 188, 156);
        doc.rect(110, 182, 90, 6, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text('DÉTAILS LOCATION', 155, 186, { align: 'center' }, { font: 'bold' });

        const duration = Math.ceil((new Date(reservation.endDate) - new Date(reservation.startDate)) / (1000 * 60 * 60 * 24));
        doc.setTextColor(44, 62, 80);
        doc.setFontSize(10);
        doc.text(`(${duration} JOURS)`, 155, 194, { align: 'center' });
        doc.setFontSize(8);
        doc.text('Prix supplémentaire:', 112, 200);
        doc.text('AUCUN', 195, 200, { align: 'right' });
        doc.text('Lieu de départ:', 112, 206);
        doc.text(reservation.pickupLocation || user?.companyInfo?.bureau || 'Bureau Principal', 195, 206, { align: 'right' });
        doc.text('Lieu de retour:', 112, 212);
        doc.text(reservation.returnLocation || user?.companyInfo?.bureau || 'Bureau Principal', 195, 212, { align: 'right' });

        // Disclaimer with warning icon - smaller
        doc.setFillColor(255, 193, 7);
        doc.rect(10, 220, 190, 8, 'F');
        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text('IMPORTANT: Le locataire est seul responsable des infractions de circulation et de toute poursuite légale ou douanière à son encontre.', 105, 225, { align: 'center' });

        // Declaration - smaller
        doc.setFillColor(236, 240, 241);
        doc.rect(10, 232, 190, 6, 'F');
        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text('Je déclare avoir lu les conditions générales mentionnées dans le contrat de location de voiture et les accepter sans réserve.', 105, 235, { align: 'center' });

        // Date and Location - smaller
        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text(`À Ville le ${new Date().toLocaleDateString('fr-FR')}`, 105, 245, { align: 'center' });

        // Signatures - smaller
        doc.setDrawColor(52, 152, 219);
        doc.setLineWidth(0.3);
        doc.rect(10, 248, 90, 20);
        doc.rect(110, 248, 90, 20);

        doc.setTextColor(44, 62, 80);
        doc.setFontSize(8);
        doc.text('Signature Client', 55, 260, { align: 'center' });
        doc.text('SIGNATURE ENTREPRISE', 155, 260, { align: 'center' });

        // Save the PDF
        doc.save(`contract-${reservation._id.slice(-8)}.pdf`);

        // Add a small delay between downloads to prevent browser issues
        if (i < filteredReservations.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      toast.success('All selected contracts downloaded successfully!');
    } catch (error) {
      console.error('Error generating contracts:', error);
      toast.error('Error generating contracts');
    }
  };

  // Contract Modal
  const renderContractModal = () => {
    if (!selectedReservation) return null;

    const duration = Math.ceil((new Date(selectedReservation.endDate) - new Date(selectedReservation.startDate)) / (1000 * 60 * 60 * 24));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-screen overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contrat de Location de Voiture</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setShowContractModal(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
              <button
                onClick={() => downloadContract(selectedReservation)}
                className="btn btn-info"
              >
                Télécharger PDF
              </button>
            </div>
          </div>

          {/* Contract Content */}
          <div className="p-6">
            <div className="bg-white border border-gray-300 rounded-lg p-8 max-w-4xl mx-auto shadow-sm">
              {/* Title */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">LOCATION DE VOITURE sans chauffeur</h1>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Contrat N° : {selectedReservation._id.slice(-8).toUpperCase()}</span>
                  <span>Date : {new Date().toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              {/* Top Row */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Renter Information */}
                <div className="border border-gray-300 rounded-lg shadow-sm">
                  <div className="bg-gray-50 border-b border-gray-300 p-3 text-center font-bold text-gray-700 rounded-t-md">
                    DÉTAILS DU CLIENT
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-bold">Nom :</span>
                      <span>{selectedReservation.client?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Date de naissance :</span>
                      <span>{selectedReservation.client?.dateOfBirth ? new Date(selectedReservation.client.dateOfBirth).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">C.I.N :</span>
                      <span>{selectedReservation.client?.cin || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Pays :</span>
                      <span>{selectedReservation.client?.address?.country || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Permis de conduire N° :</span>
                      <span>{selectedReservation.client?.driverLicenseNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Adresse :</span>
                      <span>{selectedReservation.client?.address?.street ? `${selectedReservation.client.address.street}, ${selectedReservation.client.address.city}, ${selectedReservation.client.address.state} ${selectedReservation.client.address.zipCode}` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Téléphone :</span>
                      <span>{selectedReservation.client?.phone || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Email :</span>
                      <span>{selectedReservation.client?.email || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="border border-gray-300 rounded-lg shadow-sm">
                  <div className="bg-gray-50 border-b border-gray-300 p-3 text-center font-bold text-gray-700 rounded-t-md">
                    INFORMATIONS ENTREPRISE
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-bold">Agence :</span>
                      <span>{user?.companyInfo?.agence || 'Agence'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Bureau :</span>
                      <span>{user?.companyInfo?.bureau || 'Bureau Principal'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Entreprise :</span>
                      <span>{user?.companyInfo?.entreprise || 'Gestionnaire Location'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Adresse :</span>
                      <span>{user?.companyInfo?.adresse || '123 Rue Principale'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Ville :</span>
                      <span>{user?.companyInfo?.ville || 'Ville - 12345'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Agent:</span>
                      <span>{user?.companyInfo?.agent || 'Gestionnaire Location'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Phone:</span>
                      <span>{user?.companyInfo?.telephone || '+1 (555) 123-4567'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-bold">Email:</span>
                      <span>{user?.companyInfo?.email || 'info@carrental.com'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Middle Row */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Second Driver Information */}
                <div className="border border-gray-300 rounded-lg shadow-sm">
                  <div className="bg-gray-50 border-b border-gray-300 p-3 text-center font-bold text-gray-700 rounded-t-md">
                    SECOND DRIVER
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">Name:</span>
                      <span>NONE</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Date of Birth:</span>
                      <span>N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">ID/Passport:</span>
                      <span>N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Country:</span>
                      <span>N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Driving License N°:</span>
                      <span>N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Address:</span>
                      <span>N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Phone (1):</span>
                      <span>N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Phone (2):</span>
                      <span>N/A</span>
                    </div>
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="border border-gray-300 rounded-lg shadow-sm">
                  <div className="bg-gray-50 border-b border-gray-300 p-3 text-center font-bold text-gray-700 rounded-t-md">
                    VEHICLE INFORMATION
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">Make/Model:</span>
                      <span>{(selectedReservation.car?.brand && selectedReservation.car?.model) ? `${selectedReservation.car.brand}/${selectedReservation.car.model}` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Code/Registration:</span>
                      <span>{selectedReservation.car?.licensePlate || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Fuel Type:</span>
                      <span>GASOLINE</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Starting Mileage:</span>
                      <span>{selectedReservation.initialMileage || '0'} Km</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Return Mileage:</span>
                      <span>0 Km</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Vehicle Deductible:</span>
                      <span>500 MAD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Payment Method:</span>
                      <span>Paid in case of damage</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Rental Information */}
                <div className="border border-gray-300 rounded-lg shadow-sm">
                  <div className="bg-gray-50 border-b border-gray-300 p-3 text-center font-bold text-gray-700 rounded-t-md">
                    RENTAL INFORMATION
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">Reservation Price:</span>
                      <span>${selectedReservation.totalPrice?.toFixed(2) || '0.00'} (${selectedReservation.dailyPrice?.toFixed(2) || '0.00'}/day)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Remaining to Pay:</span>
                      <span>${selectedReservation.totalPrice?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Departure Date & Time:</span>
                      <span>{new Date(selectedReservation.startDate).toLocaleDateString('en-GB')} at 14:30</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Return Date & Time:</span>
                      <span>{new Date(selectedReservation.endDate).toLocaleDateString('en-GB')} at 14:30</span>
                    </div>
                  </div>
                </div>

                {/* Rental Details */}
                <div className="border border-gray-300 rounded-lg shadow-sm">
                  <div className="bg-gray-50 border-b border-gray-300 p-3 text-center font-bold text-gray-700 rounded-t-md">
                    RENTAL DETAILS
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="text-center text-lg font-bold">
                      ({duration} DAYS)
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Additional Price:</span>
                      <span>NONE</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Departure Location:</span>
                      <span>{selectedReservation.pickupLocation || user?.companyInfo?.bureau || 'Bureau Principal'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Return Location:</span>
                      <span>{selectedReservation.returnLocation || user?.companyInfo?.bureau || 'Bureau Principal'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="border border-gray-300 rounded-lg p-4 mb-6 bg-gray-50">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-gray-600 text-xl">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-700">
                      <strong>IMPORTANT:</strong> The renter is solely responsible for traffic offenses and any legal or customs prosecutions against them.
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Services */}


              {/* Declaration */}
              <div className="border border-gray-300 rounded-lg p-4 mb-6 bg-gray-50">
                <p className="text-sm text-gray-700 text-center">
                  I declare that I have read the general conditions mentioned in the car rental contract and accept them without reservation.
                </p>
              </div>

              {/* Date and Location */}
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  À {user?.companyInfo?.ville || 'Ville'} le {new Date().toLocaleDateString('fr-FR')}
                </p>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-6">
                <div className="border border-gray-300 rounded-lg p-4 text-center shadow-sm">
                  <div className="h-20 border-2 border-dashed border-gray-300 mb-2 flex items-center justify-center">
                    <span className="text-gray-500">Client Signature</span>
                  </div>
                  <p className="text-sm text-gray-600">Client Signature</p>
                </div>
                <div className="border border-gray-300 rounded-lg p-4 text-center shadow-sm">
                  <div className="h-20 border-2 border-dashed border-gray-300 mb-2 flex items-center justify-center">
                    <span className="text-gray-500">Company Signature</span>
                  </div>
                  <p className="text-sm text-gray-600">COMPANY SIGNATURE</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Réservations</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Gérez les réservations et locations de voitures</p>

          {/* 🆕 ALERTE PHOTOS AVANT */}
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  📸 Photos Avant
                </h3>
                <div className="mt-1 text-xs sm:text-sm text-amber-700 dark:text-amber-300">
                  <p>
                    <strong>Attention :</strong> Les photos "Avant" seront automatiquement supprimées si vous supprimez cette réservation (immédiatement) ou si elle est terminée (+1 jour de délai).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === 'admin' && (
            <>


              <button
                onClick={downloadAllContracts}
                className="btn btn-info flex items-center gap-2 text-sm"
                title="Download contracts for all reservations"
              >
                <FiDownload className="w-4 h-4" />
                <span className="hidden sm:inline">Télécharger Tous les Contrats</span>
                <span className="sm:hidden">Contrats</span>
              </button>
            </>
          )}
          {(user?.role === 'admin' || user?.role === 'staff') && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn bg-brand-green hover:bg-green-700 text-white shadow-md flex items-center gap-2 transition-all text-sm"
            >
              <FiPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Nouvelle Réservation</span>
              <span className="sm:hidden">Nouveau</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4 text-center border-t-4 border-yellow-500 hover:shadow-lg transition-transform hover:-translate-y-1">
          <div className="text-2xl font-bold text-yellow-600">{reservations.filter(r => r.status === 'Pending').length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">En attente</div>
        </div>
        <div className="card p-4 text-center border-t-4 border-brand-green hover:shadow-lg transition-transform hover:-translate-y-1">
          <div className="text-2xl font-bold text-brand-green">{reservations.filter(r => r.status === 'Active').length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">En cours</div>
        </div>
        <div className="card p-4 text-center border-t-4 border-red-500 hover:shadow-lg transition-transform hover:-translate-y-1">
          <div className="text-2xl font-bold text-red-600">{reservations.filter(r => r.paymentStatus === 'Unpaid').length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Non payé</div>
        </div>
        <div className="card p-4 text-center border-t-4 border-gray-500 hover:shadow-lg transition-transform hover:-translate-y-1">
          <div className="text-2xl font-bold text-gray-600">{reservations.filter(r => r.status === 'Completed').length}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Terminé</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher des réservations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts</option>
              <option value="Pending">En attente</option>
              <option value="Confirmed">Confirmé</option>
              <option value="Active">En cours</option>
              <option value="Completed">Terminé</option>
              <option value="Cancelled">Annulé</option>
              <option value="Overdue">En retard</option>
            </select>
            <select
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">Tous les statuts de paiement</option>
              <option value="Unpaid">Non payé</option>
              <option value="Partial">Partiel</option>
              <option value="Paid">Payé</option>
              <option value="Refunded">Remboursé</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reservations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReservations.map((reservation) => (
          <div key={reservation._id} className={`card p-6 hover:shadow-xl transition-all duration-300 border-t-4 group relative overflow-hidden ${reservation.status === 'Active' ? 'border-brand-green' :
            reservation.status === 'Pending' ? 'border-yellow-500' :
              reservation.status === 'Completed' ? 'border-gray-500' :
                'border-primary-500'
            }`}>
            <div className={`absolute top-0 right-0 -mt-2 -mr-2 w-16 h-16 rounded-full opacity-10 group-hover:scale-125 transition-transform ${reservation.status === 'Active' ? 'bg-green-500' :
              'bg-primary-500'
              }`}></div>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {reservation.car?.brand} {reservation.car?.model}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {reservation.client?.name}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  CIN: {reservation.client?.cin || 'N/A'}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col items-end">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                    {getStatusText(reservation.status)}
                  </span>
                  {/* Status info tooltip */}
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right flex items-center gap-1">
                    {getStatusInfo(reservation.status).icon} {getStatusInfo(reservation.status).actions}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                  {getPaymentStatusText(reservation.paymentStatus)}
                </span>
              </div>
            </div>

            <div className="space-y-3 mb-4 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiCalendar className="mr-2 text-primary-500" />
                {new Date(reservation.startDate).toLocaleDateString()} - {new Date(reservation.endDate).toLocaleDateString()}
                <span className="ml-auto px-2 py-0.5 bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs rounded-full border border-primary-100 dark:border-primary-800">
                  {Math.ceil((new Date(reservation.endDate) - new Date(reservation.startDate)) / (1000 * 60 * 60 * 24))} jours
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiDollarSign className="mr-2 text-brand-green" />
                <span className="font-semibold text-gray-900 dark:text-white mr-1">{(reservation.totalPrice?.toFixed(2) || '0.00')} MAD</span>
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  ({(reservation.dailyPrice?.toFixed(2) || '0.00')} MAD/j)
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <FiMapPin className="mr-2 text-gray-400" />
                {reservation.pickupLocation}
              </div>
              {reservation.secondDriver && (
                <div className="flex items-center text-sm text-primary-600 dark:text-primary-400">
                  <FiUsers className="mr-2" />
                  🚗 2ème: {reservation.secondDriver.name}
                </div>
              )}
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <FiUser className="mr-1.5" />
                Par: {reservation.createdBy?.name || 'Inconnu'}
                <span className="ml-auto">
                  {new Date(reservation.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Link
                to={`/reservations/${reservation._id}`}
                className="btn bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50 flex items-center text-sm font-medium px-3 py-2 rounded-lg transition-colors border border-primary-100 dark:border-primary-800"
              >
                <FiEye className="mr-1.5" />
                Détails
              </Link>
              <div className="flex gap-2">
                {/* Status Update Buttons */}
                {reservation.status === 'Pending' && (
                  <button
                    onClick={() => handleStatusUpdate(reservation._id, 'Confirmed')}
                    className="btn btn-success p-2"
                    title="Confirmer la Réservation"
                  >
                    <FiCheck className="h-4 w-4" />
                  </button>
                )}
                {reservation.status === 'Confirmed' && (
                  <button
                    onClick={() => handleStatusUpdate(reservation._id, 'Active')}
                    className="p-2 text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm transition-colors"
                    title="Démarrer la Location"
                  >
                    <FiPlay className="h-4 w-4" />
                  </button>
                )}
                {reservation.status === 'Active' && (
                  <button
                    className="btn btn-secondary p-2 opacity-50 cursor-not-allowed"
                    title="Le statut 'Completed' est appliqué automatiquement par le système lorsque la date de fin est dépassée"
                    disabled
                  >
                    <FiCheckCircle className="h-4 w-4" />
                  </button>
                )}

                {/* Payment Status Update */}
                {reservation.paymentStatus === 'Unpaid' && (
                  <button
                    onClick={() => handlePaymentUpdate(reservation._id, 'Paid')}
                    className="btn btn-success p-2"
                    title="Marquer comme Payé"
                  >
                    <FiDollarSign className="h-4 w-4" />
                  </button>
                )}

                {/* Process Payment Button */}
                {reservation.paymentStatus === 'Unpaid' && (
                  <button
                    onClick={() => openPaymentModal(reservation)}
                    className="btn btn-warning p-2"
                    title="Traiter le Paiement"
                  >
                    <FiCreditCard className="h-4 w-4" />
                  </button>
                )}

                {/* Download Contract Button */}
                <button
                  onClick={() => downloadContract(reservation)}
                  className="btn btn-info p-2"
                  title="Télécharger le Contrat"
                >
                  <FiDownload className="h-4 w-4" />
                </button>

                {/* View Contract Button */}
                <button
                  onClick={() => openContractModal(reservation)}
                  className="btn btn-outline p-2"
                  title="Voir le Contrat"
                >
                  <FiEye className="h-4 w-4" />
                </button>

                {/* Edit Button - Only show for pending reservations */}
                {reservation.status === 'Pending' && (
                  <button
                    onClick={() => handleEdit(reservation)}
                    className="btn btn-secondary p-2"
                    title="Modifier la réservation"
                  >
                    <FiEdit className="h-4 w-4" />
                  </button>
                )}

                {/* Show info for non-editable reservations */}
                {reservation.status !== 'Pending' && (
                  <button
                    className="btn btn-secondary p-2 opacity-50 cursor-not-allowed"
                    title={`Impossible de modifier une réservation ${reservation.status === 'Active' ? 'en cours' : 'terminée'}`}
                    disabled
                  >
                    <FiEdit className="h-4 w-4" />
                  </button>
                )}
                {/* Delete Button - Only for pending and completed reservations */}
                {(reservation.status === 'Pending' || reservation.status === 'Completed') && (
                  <button
                    onClick={() => handleDelete(reservation._id)}
                    className="btn btn-danger p-2"
                    title="Supprimer la réservation"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                )}

                {/* Show info for non-deletable reservations */}
                {reservation.status === 'Active' && (
                  <button
                    className="btn btn-danger p-2 opacity-50 cursor-not-allowed"
                    title="Impossible de supprimer une réservation en cours"
                    disabled
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredReservations.length === 0 && (
        <div className="text-center py-12">
          <FiCalendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Aucune réservation trouvée</p>
        </div>
      )}

      {/* Add Reservation Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Créer une Nouvelle Réservation</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Voiture*
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      value={carSearchTerm}
                      onChange={(e) => {
                        setCarSearchTerm(e.target.value);
                        setShowCarDropdown(true);
                        if (!e.target.value) {
                          setFormData({ ...formData, car: '', dailyPrice: '' });
                        }
                      }}
                      onFocus={() => setShowCarDropdown(true)}
                      placeholder="Rechercher une voiture..."
                      className={`input w-full pl-10 pr-10 ${carSearchTerm && availableCars.length === 0 && filteredCars.length > 0
                        ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
                        : ''
                        }`}
                    />
                    {carSearchTerm && (
                      <button
                        type="button"
                        onClick={clearCarSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    )}
                    {/* Warning when no available cars */}
                    {carSearchTerm && availableCars.length === 0 && filteredCars.length > 0 && (
                      <div className="absolute -bottom-8 left-0 text-xs text-red-600 dark:text-red-400">
                        ⚠️ Aucune voiture disponible trouvée
                      </div>
                    )}
                    {showCarDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredCars.length > 0 ? (
                          <>
                            {/* Summary Section */}
                            <div className="px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                              📊 Résumé: {availableCars.length} disponible{availableCars.length !== 1 ? 's' : ''} • {rentedCars.length} louée{rentedCars.length !== 1 ? 's' : ''} • {maintenanceCars.length} en maintenance
                            </div>

                            {/* Available Cars Section */}
                            {availableCars.length > 0 && (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
                                  🟢 {availableCars.length} voiture{availableCars.length > 1 ? 's' : ''} disponible{availableCars.length > 1 ? 's' : ''}
                                </div>
                                {availableCars.map((car) => (
                                  <div
                                    key={car._id}
                                    onClick={() => handleCarSelect(car)}
                                    className="px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer border-b border-gray-200 dark:border-gray-600 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {car.year} {car.brand} {car.model}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {car.licensePlate} - {car.dailyPrice} MAD/jour
                                    </div>
                                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                      ✅ Disponible pour location
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Rented Cars Section */}
                            {rentedCars.length > 0 && (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                                  🔴 {rentedCars.length} voiture{rentedCars.length > 1 ? 's' : ''} déjà louée{rentedCars.length > 1 ? 's' : ''}
                                </div>
                                {rentedCars.map((car) => (
                                  <div
                                    key={car._id}
                                    className="px-4 py-3 bg-gray-50 dark:bg-gray-700 cursor-not-allowed border-b border-gray-200 dark:border-gray-600 opacity-60"
                                  >
                                    <div className="font-medium text-gray-500 dark:text-gray-400 line-through">
                                      {car.year} {car.brand} {car.model}
                                    </div>
                                    <div className="text-sm text-gray-400 dark:text-gray-500">
                                      {car.licensePlate} - {car.dailyPrice} MAD/jour
                                    </div>
                                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                      ❌ Déjà louée - Non disponible
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Maintenance Cars Section */}
                            {maintenanceCars.length > 0 && (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                                  🟡 {maintenanceCars.length} voiture{maintenanceCars.length > 1 ? 's' : ''} en maintenance
                                </div>
                                {maintenanceCars.map((car) => (
                                  <div
                                    key={car._id}
                                    className="px-4 py-3 bg-gray-50 dark:bg-gray-700 cursor-not-allowed border-b border-gray-200 dark:border-gray-600 opacity-60"
                                  >
                                    <div className="font-medium text-gray-500 dark:text-gray-400 line-through">
                                      {car.year} {car.brand} {car.model}
                                    </div>
                                    <div className="text-sm text-gray-400 dark:text-gray-400">
                                      {car.licensePlate} - {car.dailyPrice} MAD/jour
                                    </div>
                                    <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                      🔧 En maintenance - Non disponible
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                            <FiSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Aucune voiture trouvée</p>
                            <p className="text-xs">Essayez de modifier vos critères de recherche</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Show selected car status */}
                  {formData.car && (
                    <div className="mt-2 text-xs">
                      {(() => {
                        const selectedCar = cars.find(car => car._id === formData.car);
                        if (selectedCar) {
                          if (selectedCar.status === 'Available') {
                            return <span className="text-green-600 dark:text-green-400">✅ Voiture sélectionnée: {selectedCar.brand} {selectedCar.model} - Disponible</span>;
                          } else if (selectedCar.status === 'Rented') {
                            return <span className="text-red-600 dark:text-red-400">❌ Voiture sélectionnée: {selectedCar.brand} {selectedCar.model} - Déjà louée (non disponible)</span>;
                          } else {
                            return <span className="text-yellow-600 dark:text-yellow-400">🔧 Voiture sélectionnée: {selectedCar.brand} {selectedCar.model} - En maintenance (non disponible)</span>;
                          }
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      value={clientSearchTerm}
                      onChange={(e) => {
                        setClientSearchTerm(e.target.value);
                        setShowClientDropdown(true);
                        if (!e.target.value) {
                          setFormData({ ...formData, client: '' });
                        }
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      placeholder="Rechercher un client..."
                      className="input w-full pl-10 pr-10"
                    />
                    {clientSearchTerm && (
                      <button
                        type="button"
                        onClick={clearClientSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    )}
                    {showClientDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredClients.length > 0 ? (
                          <>
                            <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                              {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} trouvé{filteredClients.length > 1 ? 's' : ''}
                            </div>
                            {filteredClients.map((client) => (
                              <div
                                key={client._id}
                                onClick={() => handleClientSelect(client)}
                                className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors"
                              >
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {client.name}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {client.email} {client.phone ? `- ${client.phone}` : ''}
                                </div>
                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {client.totalRentals || 0} location{client.totalRentals !== 1 ? 's' : ''} • {client.isBlacklisted ? 'Liste noire' : 'Actif'}
                                </div>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                            <FiSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Aucun client trouvé</p>
                            <p className="text-xs">Essayez de modifier vos critères de recherche</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              {/* Calculation Display */}
              {(calculatedValues.days > 0 && calculatedValues.totalPrice > 0) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Durée de location</div>
                      <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                        {calculatedValues.days} {calculatedValues.days === 1 ? 'Jour' : 'Jours'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Prix total</div>
                      <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                        {calculatedValues.totalPrice.toFixed(2)} MAD
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-2 text-sm text-blue-600 dark:text-blue-400">
                    {formData.dailyPrice} MAD/jour × {calculatedValues.days} jours
                  </div>
                </div>
              )}

              {/* Error message for invalid dates */}
              {formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate) && (
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="text-sm text-red-600 dark:text-red-400">
                    ⚠️ La date de fin doit être après la date de début
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Prix journalier *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.dailyPrice}
                    onChange={(e) => setFormData({ ...formData, dailyPrice: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Dépôt
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deposit}
                    onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Méthode de paiement *
                  </label>
                  <select
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="input"
                  >
                    <option value="Credit Card">Carte de crédit</option>
                    <option value="Debit Card">Carte de débit</option>
                    <option value="Cash">Espèces</option>
                    <option value="Online Payment">Paiement en ligne</option>
                    <option value="Bank Transfer">Virement bancaire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Niveau de carburant
                  </label>
                  <select
                    value={formData.fuelLevel}
                    onChange={(e) => setFormData({ ...formData, fuelLevel: e.target.value })}
                    className="input"
                  >
                    <option value="Full">Plein</option>
                    <option value="Three Quarters">Trois quarts</option>
                    <option value="Half">Moitié</option>
                    <option value="Quarter">Quart</option>
                    <option value="Empty">Vide</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lieu de départ *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.pickupLocation}
                    onChange={(e) => setFormData({ ...formData, pickupLocation: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lieu de retour *
                  </label>
                  <input
                    type="input"
                    required
                    value={formData.returnLocation}
                    onChange={(e) => setFormData({ ...formData, returnLocation: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kilométrage initial
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.initialMileage}
                  onChange={(e) => setFormData({ ...formData, initialMileage: e.target.value })}
                  className="input"
                />
              </div>

              {/* Second Driver Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="hasSecondDriver"
                    checked={formData.hasSecondDriver}
                    onChange={(e) => setFormData({ ...formData, hasSecondDriver: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="hasSecondDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    🚗 Ajouter un Deuxième Conducteur
                  </label>
                </div>

                {!formData.hasSecondDriver && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 pl-7">
                    ✓ Optionnel - Permet d'ajouter un conducteur supplémentaire à la réservation
                  </div>
                )}

                {formData.hasSecondDriver && (
                  <div className="space-y-4 pl-7 border-l-2 border-blue-200 dark:border-blue-800">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                          <FiUsers className="w-4 h-4" />
                        </div>
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                          <p className="font-medium mb-1">Deuxième Conducteur</p>
                          <p className="text-xs">
                            Sélectionnez un client différent du conducteur principal.
                            Le conducteur principal ne peut pas être sélectionné comme deuxième conducteur.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Deuxième Conducteur
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiSearch className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={secondDriverSearchTerm}
                          onChange={(e) => {
                            setSecondDriverSearchTerm(e.target.value);
                            setShowSecondDriverDropdown(true);
                          }}
                          onFocus={() => setShowSecondDriverDropdown(true)}
                          placeholder={clients.length > 0 ? "Rechercher un client existant..." : "Aucun client disponible"}
                          className="input w-full pl-10 pr-10"
                        />
                        {secondDriverSearchTerm && (
                          <button
                            type="button"
                            onClick={clearSecondDriverSearch}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            <FiX className="h-4 w-4" />
                          </button>
                        )}
                        {showSecondDriverDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filteredSecondDriverClients.length > 0 ? (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                  {secondDriverSearchTerm.trim() ?
                                    `${filteredSecondDriverClients.length} client${filteredSecondDriverClients.length > 1 ? 's' : ''} trouvé${filteredSecondDriverClients.length > 1 ? 's' : ''}` :
                                    `${filteredSecondDriverClients.length} client${filteredSecondDriverClients.length > 1 ? 's' : ''} disponible${filteredSecondDriverClients.length > 1 ? 's' : ''}`
                                  }
                                </div>
                                {filteredSecondDriverClients.map((client) => (
                                  <div
                                    key={client._id}
                                    onClick={() => handleSecondDriverSelect(client)}
                                    className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {client.name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {client.email} {client.phone ? `- ${client.phone}` : ''}
                                    </div>
                                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                      {client.totalRentals || 0} location{client.totalRentals !== 1 ? 's' : ''} • {client.isBlacklisted ? 'Liste noire' : 'Actif'}
                                    </div>
                                  </div>
                                ))}
                              </>
                            ) : (
                              <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                                <FiSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                {clients.length === 0 ? (
                                  <>
                                    <p>Aucun client enregistré</p>
                                    <p className="text-xs">Créez d'abord des clients dans la section Clients</p>
                                  </>
                                ) : (
                                  <>
                                    <p>Aucun client trouvé</p>
                                    <p className="text-xs">Essayez de modifier vos critères de recherche</p>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        ✓ Les informations du client (permis, téléphone, email) sont déjà enregistrées
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Remarques
                </label>
                <textarea
                  placeholder="Remarques supplémentaires sur la réservation..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn bg-brand-green hover:bg-green-700 text-white shadow-md transition-all">
                  Créer la Réservation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Reservation Modal */}
      {showEditModal && editingReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Modifier la Réservation</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Voiture *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      value={carSearchTerm}
                      onChange={(e) => {
                        setCarSearchTerm(e.target.value);
                        setShowCarDropdown(true);
                        if (!e.target.value) {
                          setFormData({ ...formData, car: '', dailyPrice: '' });
                        }
                      }}
                      onFocus={() => setShowCarDropdown(true)}
                      placeholder="Rechercher une voiture..."
                      className="input w-full pl-10 pr-10"
                    />
                    {carSearchTerm && (
                      <button
                        type="button"
                        onClick={clearCarSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    )}
                    {showCarDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredCars.length > 0 ? (
                          <>
                            {/* Available Cars Section */}
                            {availableCars.length > 0 && (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
                                  🟢 {availableCars.length} voiture{availableCars.length > 1 ? 's' : ''} disponible{availableCars.length > 1 ? 's' : ''}
                                </div>
                                {availableCars.map((car) => (
                                  <div
                                    key={car._id}
                                    onClick={() => handleCarSelect(car)}
                                    className="px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 cursor-pointer border-b border-gray-200 dark:border-gray-600 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {car.year} {car.brand} {car.model}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {car.licensePlate} - {car.dailyPrice} MAD/jour
                                    </div>
                                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                      ✅ Disponible pour location
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Rented Cars Section */}
                            {rentedCars.length > 0 && (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                                  🔴 {rentedCars.length} voiture{rentedCars.length > 1 ? 's' : ''} déjà louée{rentedCars.length > 1 ? 's' : ''}
                                </div>
                                {rentedCars.map((car) => (
                                  <div
                                    key={car._id}
                                    className="px-4 py-3 bg-gray-50 dark:bg-gray-700 cursor-not-allowed border-b border-gray-200 dark:border-gray-600 opacity-60"
                                  >
                                    <div className="font-medium text-gray-500 dark:text-gray-400 line-through">
                                      {car.year} {car.brand} {car.model}
                                    </div>
                                    <div className="text-sm text-gray-400 dark:text-gray-500">
                                      {car.licensePlate} - {car.dailyPrice} MAD/jour
                                    </div>
                                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                                      ❌ Déjà louée - Non disponible
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Maintenance Cars Section */}
                            {maintenanceCars.length > 0 && (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                                  🟡 {maintenanceCars.length} voiture{maintenanceCars.length > 1 ? 's' : ''} en maintenance
                                </div>
                                {maintenanceCars.map((car) => (
                                  <div
                                    key={car._id}
                                    className="px-4 py-3 bg-gray-50 dark:bg-gray-700 cursor-not-allowed border-b border-gray-200 dark:border-gray-600 opacity-60"
                                  >
                                    <div className="font-medium text-gray-500 dark:text-gray-400 line-through">
                                      {car.year} {car.brand} {car.model}
                                    </div>
                                    <div className="text-sm text-gray-400 dark:text-gray-400">
                                      {car.licensePlate} - {car.dailyPrice} MAD/jour
                                    </div>
                                    <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                                      🔧 En maintenance - Non disponible
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                            <FiSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Aucune voiture trouvée</p>
                            <p className="text-xs">Essayez de modifier vos critères de recherche</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Client *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      value={clientSearchTerm}
                      onChange={(e) => {
                        setClientSearchTerm(e.target.value);
                        setShowClientDropdown(true);
                        if (!e.target.value) {
                          setFormData({ ...formData, client: '' });
                        }
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      placeholder="Rechercher un client..."
                      className="input w-full pl-10 pr-10"
                    />
                    {showClientDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredClients.length > 0 ? (
                          filteredClients.map((client) => (
                            <div
                              key={client._id}
                              onClick={() => handleClientSelect(client)}
                              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900 dark:text-white">
                                {client.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {client.email} {client.phone ? `- ${client.phone}` : ''}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-gray-500 dark:text-gray-400">
                            Aucun client trouvé
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date de début *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date de fin *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              {/* Calculation Display */}
              {(calculatedValues.days > 0 && calculatedValues.totalPrice > 0) && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Durée de location</div>
                      <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                        {calculatedValues.days} {calculatedValues.days === 1 ? 'Jour' : 'Jours'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Prix total</div>
                      <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                        {calculatedValues.totalPrice.toFixed(2)} MAD
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-2 text-sm text-blue-600 dark:text-blue-400">
                    {formData.dailyPrice} MAD/jour × {calculatedValues.days} jours
                  </div>
                </div>
              )}

              {/* Error message for invalid dates */}
              {formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate) && (
                <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="text-sm text-red-600 dark:text-red-400">
                    ⚠️ La date de fin doit être après la date de début
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Prix journalier *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.dailyPrice}
                    onChange={(e) => setFormData({ ...formData, dailyPrice: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Dépôt
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.deposit}
                    onChange={(e) => setFormData({ ...formData, deposit: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Méthode de paiement *
                  </label>
                  <select
                    required
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="input"
                  >
                    <option value="Credit Card">Carte de crédit</option>
                    <option value="Debit Card">Carte de débit</option>
                    <option value="Cash">Espèces</option>
                    <option value="Online Payment">Paiement en ligne</option>
                    <option value="Bank Transfer">Virement bancaire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Niveau de carburant
                  </label>
                  <select
                    value={formData.fuelLevel}
                    onChange={(e) => setFormData({ ...formData, fuelLevel: e.target.value })}
                    className="input"
                  >
                    <option value="Full">Plein</option>
                    <option value="Three Quarters">Trois quarts</option>
                    <option value="Half">Moitié</option>
                    <option value="Quarter">Quart</option>
                    <option value="Empty">Vide</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lieu de départ *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.pickupLocation}
                    onChange={(e) => setFormData({ ...formData, pickupLocation: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lieu de retour *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.returnLocation}
                    onChange={(e) => setFormData({ ...formData, returnLocation: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kilométrage initial
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.initialMileage}
                  onChange={(e) => setFormData({ ...formData, initialMileage: e.target.value })}
                  className="input"
                />
              </div>

              {/* Second Driver Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="editHasSecondDriver"
                    checked={formData.hasSecondDriver}
                    onChange={(e) => setFormData({ ...formData, hasSecondDriver: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="editHasSecondDriver" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    🚗 Ajouter un Deuxième Conducteur
                  </label>
                </div>

                {!formData.hasSecondDriver && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 pl-7">
                    ✓ Optionnel - Permet d'ajouter un conducteur supplémentaire à la réservation
                  </div>
                )}

                {formData.hasSecondDriver && (
                  <div className="space-y-4 pl-7 border-l-2 border-blue-200 dark:border-blue-800">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <div className="text-blue-600 dark:text-blue-400 mt-0.5">
                          <FiUsers className="w-4 h-4" />
                        </div>
                        <div className="text-sm text-blue-800 dark:text-blue-200">
                          <p className="font-medium mb-1">Deuxième Conducteur</p>
                          <p className="text-xs">
                            Sélectionnez un client différent du conducteur principal.
                            Le conducteur principal ne peut pas être sélectionné comme deuxième conducteur.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Deuxième Conducteur
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiSearch className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={secondDriverSearchTerm}
                          onChange={(e) => {
                            setSecondDriverSearchTerm(e.target.value);
                            setShowSecondDriverDropdown(true);
                          }}
                          onFocus={() => setShowSecondDriverDropdown(true)}
                          placeholder={clients.length > 0 ? "Rechercher un client existant..." : "Aucun client disponible"}
                          className="input w-full pl-10 pr-10"
                        />
                        {secondDriverSearchTerm && (
                          <button
                            type="button"
                            onClick={clearSecondDriverSearch}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            <FiX className="h-4 w-4" />
                          </button>
                        )}
                        {showSecondDriverDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filteredSecondDriverClients.length > 0 ? (
                              <>
                                <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                  {secondDriverSearchTerm.trim() ?
                                    `${filteredSecondDriverClients.length} client${filteredSecondDriverClients.length > 1 ? 's' : ''} trouvé${filteredSecondDriverClients.length > 1 ? 's' : ''}` :
                                    `${filteredSecondDriverClients.length} client${filteredSecondDriverClients.length > 1 ? 's' : ''} disponible${filteredSecondDriverClients.length > 1 ? 's' : ''}`
                                  }
                                </div>
                                {filteredSecondDriverClients.map((client) => (
                                  <div
                                    key={client._id}
                                    onClick={() => handleSecondDriverSelect(client)}
                                    className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-b-0 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {client.name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {client.email} {client.phone ? `- ${client.phone}` : ''}
                                    </div>
                                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                      {client.totalRentals || 0} location{client.totalRentals !== 1 ? 's' : ''} • {client.isBlacklisted ? 'Liste noire' : 'Actif'}
                                    </div>
                                  </div>
                                ))}
                              </>
                            ) : (
                              <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                                <FiSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                {clients.length === 0 ? (
                                  <>
                                    <p>Aucun client enregistré</p>
                                    <p className="text-xs">Créez d'abord des clients dans la section Clients</p>
                                  </>
                                ) : (
                                  <>
                                    <p>Aucun client trouvé</p>
                                    <p className="text-xs">Essayez de modifier vos critères de recherche</p>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        ✓ Les informations du client (permis, téléphone, email) sont déjà enregistrées
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Remarques
                </label>
                <textarea
                  placeholder="Remarques supplémentaires sur la réservation..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingReservation(null);
                    resetForm();
                    setCalculatedValues({ days: 0, totalPrice: 0 });
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Mettre à jour la Réservation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Traiter le Paiement</h2>

            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                {selectedReservation.car?.brand} {selectedReservation.car?.model}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Client: {selectedReservation.client?.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                CIN: {selectedReservation.client?.cin || 'N/A'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Montant total : {selectedReservation.totalPrice?.toFixed(2)} MAD
              </p>
            </div>

            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Montant du paiement *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Méthode de paiement *
                </label>
                <select
                  required
                  value={paymentData.paymentMethod}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                  className="input w-full"
                >
                  <option value="Credit Card">Carte de crédit</option>
                  <option value="Debit Card">Carte de débit</option>
                  <option value="Cash">Espèces</option>
                  <option value="Online Payment">Paiement en ligne</option>
                  <optionn value="Bank Transfer">Virement bancaire</optionn>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Remarques
                </label>
                <textarea
                  placeholder="Remarques de paiement..."
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  className="input w-full"
                  rows="3"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedReservation(null);
                    setPaymentData({ amount: '', paymentMethod: 'Credit Card', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-success">
                  Traiter le Paiement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contract Modal */}
      {showContractModal && renderContractModal()}

      {/* Pagination Component */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center">
          <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
            {/* Previous Page Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Précédent
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNumber = index + 1;
                const isCurrentPage = pageNumber === currentPage;

                // Show first page, last page, current page, and pages around current page
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isCurrentPage
                        ? 'bg-primary-600 text-white dark:bg-primary-500'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return (
                    <span key={pageNumber} className="px-2 py-2 text-gray-400 dark:text-gray-500">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            {/* Next Page Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Pagination Info */}
      {totalReservations > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
          Affichage de {((currentPage - 1) * reservationsPerPage) + 1} à {Math.min(currentPage * reservationsPerPage, totalReservations)}
          sur {totalReservations} réservation{totalReservations !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default Reservations; 